import { api } from '../lib/api';

/**
 * PlatformService - Centralizes all API calls organized by domain
 */

// ===== AUTH & USER SERVICES =====
export const authService = {
  /**
   * User login
   */
  async login(email, password) {
    const response = await api.post('/users?operation=login', { email, password });
    return response.data;
  },

  /**
   * New user registration
   */
  async register(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  /**
   * Get current user data or by ID
   */
  async getUser(userId = null) {
    const params = userId ? `?id=${userId}` : '';
    const response = await api.get(`/users${params}`);
    return response.data;
  },

  /**
   * Send verification code for registration
   */
  async sendVerificationCode(email, name) {
    const response = await api.post('/api/auth/send-verification-code', { email, name });
    return response.data;
  },

  /**
   * Verify registration code
   */
  async verifyRegistrationCode(email, code) {
    const response = await api.post('/api/auth/verify-registration-code', { email, code });
    return response.data;
  },

  /**
   * Send password reset code
   */
  async sendPasswordResetCode(email) {
    const response = await api.post('/api/auth/send-password-reset-code', { email });
    return response.data;
  },

  /**
   * Verify password reset code
   */
  async verifyPasswordResetCode(email, code) {
    const response = await api.post('/api/auth/verify-password-reset-code', { email, code });
    return response.data;
  },

  /**
   * Reset password with verified token
   */
  async resetPassword(email, code, newPassword) {
    const response = await api.post('/api/auth/reset-password', {
      email,
      code,
      new_password: newPassword,
    });
    return response.data;
  }
};

// ===== SUBSCRIPTION SERVICES =====
export const subscriptionService = {
  /**
   * Get subscription summary
   */
  async getSubscriptionSummary() {
    const response = await api.get('/api/subscription/summary');
    return response.data;
  },

  /**
   * Add token package
   */
  async addTokenPackage() {
    const response = await api.post('/api/subscription/add-package');
    return response.data;
  },

  /**
   * Create Stripe Checkout Session for new subscription
   */
  async createCheckoutSession(plan) {
    const response = await api.post('/api/stripe/create-checkout-session', { 
      plan,
      success_url: `${window.location.origin}/account?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/account`
    });
    return response.data;
  },

  /**
   * Create Stripe Customer Portal session for managing subscription
   */
  async createPortalSession() {
    const response = await api.post('/api/stripe/create-portal-session', {
      return_url: `${window.location.origin}/account`
    });
    return response.data;
  },

  /**
   * Create payment for token package
   */
  async purchaseTokenPackage() {
    const response = await api.post('/api/stripe/purchase-token-package', {
      success_url: `${window.location.origin}/account?package_success=true`,
      cancel_url: `${window.location.origin}/account`
    });
    return response.data;
  },

  /**
   * Helper functions for formatting
   */
  helpers: {
    // Formatar valor em BRL
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    // Calcular percentual de uso
    calculateUsagePercentage(used, limit) {
      if (!limit || limit === 0) return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },

    // Formatar tokens grandes
    formatTokens(tokens) {
      if (tokens >= 1000000) {
        return `${(tokens / 1000000).toFixed(2)}M`;
      } else if (tokens >= 1000) {
        return `${(tokens / 1000).toFixed(1)}K`;
      }
      return tokens.toString();
    }
  }
};

// ===== SYSTEM SERVICES =====
export const systemService = {
  /**
   * Check API status
   */
  async getStatus() {
    const response = await api.get('/status');
    return response.data;
  }
};

// ===== CONTENT GENERATION SERVICES =====
export const contentGenerationService = {
  /**
   * Create new content batch
   */
  async createBatch(batchData) {
    const response = await api.post('/api/contents/batches', batchData);
    return response.data;
  },

  /**
   * List user batches
   */
  async listBatches(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const url = `/api/contents/batches?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get specific batch
   */
  async getBatch(batchId) {
    const response = await api.get(`/api/contents/batches/${batchId}`);
    return response.data;
  },

  /**
   * Delete batch
   */
  async deleteBatch(batchId) {
    const response = await api.delete(`/api/contents/batches/${batchId}`);
    return response.data;
  },

  /**
   * Get content status
   */
  async getContentStatus(contentId, batchId) {
    const response = await api.get(`/api/contents/${contentId}/status?batch_id=${batchId}`);
    return response.data;
  }
};

// Default export maintaining compatibility
export default {
  auth: authService,
  system: systemService,
  subscription: subscriptionService,
  contentGeneration: contentGenerationService
};